import {
  defaultViewerTz,
  isDateOnlyISO,
  localDateKey,
  localTime,
  parseISODate,
  type PlainDate,
} from '@viquard/calendar';

export {
  defaultViewerTz,
  isDateOnlyISO,
  localDateKey,
  localTime,
  parseISODate,
};
export type { PlainDate };

type DatePreset = 'medium' | 'long' | 'month-day';
type DateTimePreset = 'medium-short' | 'long-short' | 'month-day-short';
type RelativeStyle = 'long' | 'short';
type RoundedDurationUnit = 'lessThanMinute' | 'minutes' | 'hours' | 'days';

export interface RoundedDurationSeconds {
  unit: RoundedDurationUnit;
  count: number;
}

const RELATIVE_UNITS: readonly {
  unit: Intl.RelativeTimeFormatUnit;
  seconds: number;
}[] = [
  { unit: 'year', seconds: 31_536_000 },
  { unit: 'month', seconds: 2_592_000 },
  { unit: 'week', seconds: 604_800 },
  { unit: 'day', seconds: 86_400 },
  { unit: 'hour', seconds: 3_600 },
  { unit: 'minute', seconds: 60 },
];

export function parseDate(value: string): Date | null {
  const ts = Date.parse(value);
  return Number.isNaN(ts) ? null : new Date(ts);
}

export function formatDate(value: string, locale?: string, preset: DatePreset = 'medium'): string {
  const date = parseDate(value);
  if (date === null) return value;
  try {
    return new Intl.DateTimeFormat(locale, dateOptions(preset)).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

export function formatDateTime(
  value: string,
  locale?: string,
  preset: DateTimePreset = 'medium-short',
): string {
  const date = parseDate(value);
  if (date === null) return value;
  try {
    return new Intl.DateTimeFormat(locale, dateTimeOptions(preset)).format(date);
  } catch {
    return date.toISOString();
  }
}

export function formatTime(value: string, locale?: string): string {
  const date = parseDate(value);
  if (date === null) return value;
  try {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return value;
  }
}

export function formatDateTimeRange(startsAt: string, endsAt: string, locale?: string): string {
  const start = parseDate(startsAt);
  const end = parseDate(endsAt);
  if (start === null || end === null) return `${startsAt} - ${endsAt}`;
  try {
    const formatter = new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${formatter.format(start)} - ${formatter.format(end)}`;
  } catch {
    return `${startsAt} - ${endsAt}`;
  }
}

export function formatRelativeTime(
  value: string,
  locale?: string,
  opts: { style?: RelativeStyle; now?: number } = {},
): string {
  const date = parseDate(value);
  if (date === null) return value;
  const now = opts.now ?? Date.now();
  const diffSeconds = Math.round((date.getTime() - now) / 1000);
  const absSeconds = Math.abs(diffSeconds);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, {
      numeric: 'auto',
      style: opts.style ?? 'long',
    });
    if (absSeconds < 45) return rtf.format(0, 'second');
    const match = RELATIVE_UNITS.find((entry) => absSeconds >= entry.seconds);
    if (match === undefined) return rtf.format(Math.round(diffSeconds / 60), 'minute');
    return rtf.format(Math.round(diffSeconds / match.seconds), match.unit);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

export function durationMinutes(startsAt: string, endsAt: string): number {
  const start = Date.parse(startsAt);
  const end = Date.parse(endsAt);
  if (Number.isNaN(start) || Number.isNaN(end)) return 1;
  return Math.max(1, Math.round((end - start) / 60_000));
}

export function roundedDurationSeconds(seconds: number): RoundedDurationSeconds {
  if (seconds < 60) return { unit: 'lessThanMinute', count: 0 };
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return { unit: 'minutes', count: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { unit: 'hours', count: hours };
  return { unit: 'days', count: Math.floor(hours / 24) };
}

export function viewerDateKey(value: string, viewerTz = defaultViewerTz()): string {
  return localDateKey(value, viewerTz);
}

export function viewerClockTime(value: string, viewerTz = defaultViewerTz()): string | null {
  return localTime(value, viewerTz);
}

function dateOptions(preset: DatePreset): Intl.DateTimeFormatOptions {
  switch (preset) {
    case 'long':
      return { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
    case 'month-day':
      return { month: 'short', day: 'numeric' };
    default:
      return { dateStyle: 'medium' };
  }
}

function dateTimeOptions(preset: DateTimePreset): Intl.DateTimeFormatOptions {
  switch (preset) {
    case 'long-short':
      return { dateStyle: 'long', timeStyle: 'short' };
    case 'month-day-short':
      return { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    default:
      return { dateStyle: 'medium', timeStyle: 'short' };
  }
}
